import { BacktestFormModel } from './BacktestFormModel';
import { BacktestJobsListModel } from './BacktestJobsListModel';
import { BacktestResultsModel } from './BacktestResultsModel';
import type { BacktestWidgetModel, BacktestView, NotifyBacktestError } from './index';
import { signal, computed, effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import type {
    BacktestExecuteRequest,
    BacktestJob,
    BacktestJobRecord,
    BacktestSpec,
    BacktestSpecPatch,
    NewBacktestSpec,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import { AxiosError } from 'axios';
import { inject, injectable } from 'src/features/ioc';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { BacktestWidgetAdapter } from 'src/models/BacktestWidgetAdapter';
import type { ChannelSlice } from 'src/models/ChannelSlice';
import type { SearchAlphaWidgetModel } from 'src/models/SearchAlphaWidgetModel';
import type { WidgetHeaderElement } from 'src/models/WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS, STANDALONE_COMMON_MENU_HEADER_ELEMENTS } from 'src/models/WidgetModel/impl';
import type { WidgetTabSlice } from 'src/models/WidgetTabSlice';
import {
    getBacktestSpecByIdQuery,
    getCreateBacktestSpecMutation,
    getDeleteBacktestSpecMutation,
    getExecuteBacktestMutation,
    getUpdateBacktestSpecMutation,
} from 'src/queries/backtestSpecs';
import type { ReactBindings } from 'src/types/bindings';
import type { BacktestViewMode } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

/** Constant used to indicate standalone mode when passed to init() */
export const STANDALONE_MODE_ID = 'standalone';

/**
 * Extract logging-safe fields from a caught error. Avoids dumping the raw
 * AxiosError, which can carry auth headers and full request bodies.
 */
function sanitizeErrorForLog(e: unknown): {
    message: string;
    code?: string;
    status?: number;
    requestId?: string;
} {
    if (e instanceof AxiosError) {
        const reqId = e.config?.headers?.['x-request-id'];
        const resId = e.response?.headers?.['x-request-id'];
        const requestId = typeof reqId === 'string' ? reqId : typeof resId === 'string' ? resId : undefined;
        return {
            message: e.message,
            code: e.code ?? e.name,
            status: e.response?.status,
            requestId,
        };
    }
    if (e instanceof Error) {
        return { message: e.message, code: e.name };
    }
    return { message: String(e) };
}

/**
 * Widget-level state managed by the impl reducer.
 *
 * `formModel`, `jobsListModel`, and `resultsModel` are owned sub-models held in
 * state so the reducer can drive imperative side effects (e.g. start/stop the
 * jobs-list poll and clear results on view mode change). `isRunning` is kept
 * as a plain signal on the impl, since it tracks the in-flight run mutation
 * rather than any reducer-managed state.
 */
interface BacktestImplState {
    readonly formModel: BacktestFormModel;
    readonly jobsListModel: BacktestJobsListModel;
    readonly resultsModel: BacktestResultsModel;
    readonly view: BacktestView;
    readonly loadedBacktestId: string | null;
    readonly selectedBacktest: { id: string } | null;
}

type BacktestImplAction =
    | { type: 'SWITCH_LIST_VIEW' }
    | { type: 'SWITCH_RESULTS_VIEW'; jobId: string }
    | { type: 'SWITCH_WIZARD_VIEW' }
    | { type: 'SET_LOADED_BACKTEST_ID'; id: string | null };

function backtestImplReducer(state: BacktestImplState, action: BacktestImplAction): BacktestImplState {
    switch (action.type) {
        case 'SWITCH_LIST_VIEW':
            state.jobsListModel.startPolling();
            state.resultsModel.clear();
            return { ...state, view: { mode: 'list' } };
        case 'SWITCH_RESULTS_VIEW':
            state.jobsListModel.stopPolling();
            return { ...state, view: { mode: 'results', jobId: action.jobId } };
        case 'SWITCH_WIZARD_VIEW':
            state.jobsListModel.stopPolling();
            state.resultsModel.clear();
            return { ...state, view: { mode: 'wizard' } };
        case 'SET_LOADED_BACKTEST_ID':
            return {
                ...state,
                loadedBacktestId: action.id,
                selectedBacktest: action.id ? { id: action.id } : null,
            };
        default:
            throw new UnreachableCaseError(action);
    }
}

@injectable()
export class BacktestWidgetModelImpl implements BacktestWidgetModel {
    readonly #disposableStack = new DisposableStack();

    /** Widget-level reducer state: formModel + jobsListModel + viewMode + loadedBacktestId + selectedBacktest. */
    readonly #state: Signal<BacktestImplState>;

    /** True while the run mutation (save-if-needed + execute) is in flight. */
    readonly #isRunning = signal<boolean>(false);

    // Fine-grained computeds over state slots, used internally for reactivity.
    readonly #loadedBacktestId: ReadonlySignal<string | null>;
    readonly #selectedBacktest: ReadonlySignal<{ id: string } | null>;

    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #adapter: BacktestWidgetAdapter;
    #isStandaloneMode = false;
    #notifyError!: NotifyBacktestError;

    readonly #createSpecMutation: MutationObserver<BacktestSpec, Error, NewBacktestSpec>;
    readonly #updateSpecMutation: MutationObserver<BacktestSpec, Error, { id: string; patch: BacktestSpecPatch }>;
    readonly #deleteSpecMutation: MutationObserver<void, Error, string>;
    readonly #executeBacktestMutation: MutationObserver<BacktestJob, Error, BacktestExecuteRequest>;

    headerElements!: WidgetHeaderElement[];
    eventBus!: BacktestWidgetModel['eventBus'];

    readonly view: ReadonlySignal<BacktestView>;
    readonly isRunning: ReadonlySignal<boolean> = this.#isRunning;

    /** Backwards-compat signal exposed on the event bus for widget header consumers. */
    readonly #viewModeForEvents: ReadonlySignal<BacktestViewMode>;

    // Portal target registry for rendering React components inside Svelte steps
    readonly #portalTargets = signal<ReadonlyMap<string, HTMLElement>>(new Map());
    readonly portalTargets: ReadonlySignal<ReadonlyMap<string, HTMLElement>> = this.#portalTargets;

    /** Stable form-model reference, sourced from the state slot. */
    get formModel(): BacktestFormModel {
        return this.#state.peek().formModel;
    }

    /** Stable jobs-list-model reference, sourced from the state slot. */
    get jobsListModel(): BacktestJobsListModel {
        return this.#state.peek().jobsListModel;
    }

    /** Stable results-model reference, sourced from the state slot. */
    get resultsModel(): BacktestResultsModel {
        return this.#state.peek().resultsModel;
    }

    get entrySearchModel(): SearchAlphaWidgetModel | null {
        return this.formModel.entrySearchModel;
    }

    setEntrySearchModel(model: SearchAlphaWidgetModel): void {
        this.formModel.setEntrySearchModel(model);
    }

    constructor(
        @inject('BacktestWidgetAdapter') adapter: BacktestWidgetAdapter,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('Logger') private logger: ReactBindings['Logger'],
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
    ) {
        this.#adapter = adapter;
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        this.#createSpecMutation = new MutationObserver(
            this.queryClient,
            getCreateBacktestSpecMutation(this.queryClient),
        );
        this.#updateSpecMutation = new MutationObserver(
            this.queryClient,
            getUpdateBacktestSpecMutation(this.queryClient),
        );
        this.#deleteSpecMutation = new MutationObserver(
            this.queryClient,
            getDeleteBacktestSpecMutation(this.queryClient),
        );
        this.#executeBacktestMutation = new MutationObserver(
            this.queryClient,
            getExecuteBacktestMutation(this.queryClient),
        );

        const formModel = new BacktestFormModel(logger, queryClient, formulaService);
        this.#disposableStack.use(formModel);

        const jobsListModel = new BacktestJobsListModel(featureFlagService, logger, queryClient);
        this.#disposableStack.use(jobsListModel);

        const resultsModel = new BacktestResultsModel(logger, queryClient);
        this.#disposableStack.use(resultsModel);

        this.#state = signal<BacktestImplState>({
            formModel,
            jobsListModel,
            resultsModel,
            view: { mode: 'wizard' },
            loadedBacktestId: null,
            selectedBacktest: null,
        });

        this.view = computed(() => this.#state.value.view);
        this.#viewModeForEvents = computed(() => this.#state.value.view.mode);
        this.#loadedBacktestId = computed(() => this.#state.value.loadedBacktestId);
        this.#selectedBacktest = computed(() => this.#state.value.selectedBacktest);
    }

    run(): void {
        void this.#runBacktest();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    #dispatch(action: BacktestImplAction): void {
        this.#state.value = backtestImplReducer(this.#state.value, action);
    }

    init(tabId: string, notifyError: NotifyBacktestError): void {
        this.#isStandaloneMode = tabId === STANDALONE_MODE_ID;
        this.#notifyError = notifyError;

        const persisted = this.#adapter.getState();
        if (persisted.loadedBacktestId) {
            this.#dispatch({ type: 'SET_LOADED_BACKTEST_ID', id: persisted.loadedBacktestId });
        }

        this.#disposableStack.use(this.#adapter);
        this.#channelSlice.init(tabId);
        this.#disposableStack.use(this.#channelSlice);
        this.#widgetTabSlice.init(tabId);
        this.#disposableStack.use(this.#widgetTabSlice);

        // Auto-persist loadedBacktestId changes to adapter
        this.#disposableStack.defer(
            effect(() => {
                const loadedBacktestId = this.#loadedBacktestId.value;
                queueMicrotask(() => {
                    this.#adapter.persist({ loadedBacktestId });
                });
            }),
        );

        const baseElements = this.#isStandaloneMode
            ? STANDALONE_COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker')
            : COMMON_MENU_HEADER_ELEMENTS;

        this.headerElements = [
            ...baseElements,
            { type: 'backtestName', position: 20, priority: 5 },
            { type: 'backtestSave', position: 25, priority: 5 },
            { type: 'backtestEdit', position: 30, priority: 5 },
            {
                type: 'backtestMenuItems',
                position: 2,
                overflowOnly: true,
                items: ['new', 'open', 'save', 'copy', 'delete', 'allBacktests'],
            },
        ];

        this.eventBus = this.#buildEventBus();

        if (this.#isStandaloneMode) {
            (this.#widgetTabSlice.eventBus.getSignal('widget::name') as Signal<string>).value = 'Backtest';
        }

        // Override widget::name to show the backtest name in the nameOverflowMenu trigger
        this.#disposableStack.defer(
            effect(() => {
                const name = this.formModel.name.value || 'Untitled Backtest';
                (this.#widgetTabSlice.eventBus.getSignal('widget::name') as Signal<string>).value = name;
            }),
        );

        const persistedId = this.#loadedBacktestId.peek();
        if (persistedId) {
            void this.#loadBacktestById(persistedId);
        }
    }

    registerPortalTarget(slotId: string, element: HTMLElement): void {
        const next = new Map(this.#portalTargets.value);
        next.set(slotId, element);
        this.#portalTargets.value = next;
    }

    unregisterPortalTarget(slotId: string): void {
        if (!this.#portalTargets.value.has(slotId)) return;
        const next = new Map(this.#portalTargets.value);
        next.delete(slotId);
        this.#portalTargets.value = next;
    }

    async #runBacktest(): Promise<void> {
        this.#isRunning.value = true;
        try {
            const { loadedId, isDirty, name } = untracked(() => ({
                loadedId: this.#loadedBacktestId.value,
                isDirty: this.formModel.isDirty.value,
                name: this.formModel.name.value || 'Untitled Backtest',
            }));

            let specId: string;
            try {
                specId = loadedId != null && !isDirty ? loadedId : await this.#saveBacktestAs(name);
            } catch (e) {
                this.logger.error('Backtest save during run failed', {
                    specId: loadedId,
                    error: sanitizeErrorForLog(e),
                });
                if (e instanceof AxiosError) {
                    this.#notifyError('save-error', e);
                }
                return;
            }

            try {
                const job = await this.#executeBacktestMutation.mutate({ specId });
                this.logger.info('Backtest execution started', { jobId: job.id, specId });
                this.#dispatch({ type: 'SWITCH_LIST_VIEW' });
            } catch (e) {
                this.logger.error('Backtest run failed', { specId, error: sanitizeErrorForLog(e) });
                if (e instanceof AxiosError) {
                    this.#notifyError('run-error', e);
                }
            }
        } finally {
            this.#isRunning.value = false;
        }
    }

    async #saveBacktest(): Promise<void> {
        this.formModel.markAllStepsValidated();
        const { errors, id, name, patch } = untracked(() => ({
            errors: this.formModel.currentStepErrors.value,
            id: this.#loadedBacktestId.value,
            name: this.formModel.name.value || 'Untitled Backtest',
            patch: this.formModel.buildNewSpec(),
        }));

        if (errors.length > 0) {
            throw new Error('Cannot save backtest: form validation failed');
        }

        if (!id) {
            await this.#saveBacktestAs(name);
            return;
        }

        try {
            await this.#updateSpecMutation.mutate({ id, patch });
        } catch (e) {
            if (e instanceof AxiosError && e.response?.status === 404) {
                this.logger.warn('Backtest spec deleted server-side; resetting to blank', { id });
                this.#createNewBacktest();
            }
            throw e;
        }
        this.formModel.markSaved();
        this.logger.info('Backtest saved', { id });
    }

    async #saveBacktestAs(name: string): Promise<string> {
        this.formModel.markAllStepsValidated();
        this.formModel.setName(name);
        const { errors, newSpec } = untracked(() => ({
            errors: this.formModel.currentStepErrors.value,
            newSpec: this.formModel.buildNewSpec(),
        }));

        if (errors.length > 0) {
            throw new Error('Cannot save backtest: form validation failed');
        }

        const created = await this.#createSpecMutation.mutate(newSpec);
        this.#dispatch({ type: 'SET_LOADED_BACKTEST_ID', id: created.id });
        this.formModel.markSaved();
        this.logger.info('Backtest saved as', { id: created.id, name });
        return created.id;
    }

    #createNewBacktest(): void {
        this.formModel.reset();
        this.resultsModel.clear();
        this.#dispatch({ type: 'SET_LOADED_BACKTEST_ID', id: null });
    }

    async #deleteBacktest(id: string): Promise<void> {
        try {
            await this.#deleteSpecMutation.mutate(id);
            if (this.#loadedBacktestId.peek() === id) {
                this.#createNewBacktest();
                this.#dispatch({ type: 'SWITCH_WIZARD_VIEW' });
            }
            this.logger.info('Backtest deleted', { id });
        } catch (e) {
            this.logger.error('Failed to delete backtest', { error: e instanceof Error ? e.message : String(e) });
        }
    }

    async #loadBacktestById(specId: string): Promise<void> {
        if (TRACE) this.logger.trace('Loading backtest by ID', { specId });
        try {
            const spec = await this.queryClient.fetchQuery(getBacktestSpecByIdQuery(specId));
            if (!spec) {
                this.logger.warn('Backtest spec not found; resetting to blank', { specId });
                this.#createNewBacktest();
                return;
            }
            if (TRACE) this.logger.trace('Backtest spec fetched, hydrating state', { specId, name: spec.name });
            this.formModel.hydrate(spec);
            this.#dispatch({ type: 'SET_LOADED_BACKTEST_ID', id: spec.id });
            if (TRACE) {
                this.logger.trace('Backtest state hydrated', { specId, backtestName: this.formModel.name.peek() });
            }
        } catch (e) {
            this.logger.error('Failed to load backtest; resetting to blank', {
                specId,
                error: sanitizeErrorForLog(e),
            });
            this.#createNewBacktest();
        }
    }

    async #deleteJob(id: string): Promise<void> {
        try {
            await this.jobsListModel.deleteJob(id);
        } catch {
            // jobsListModel already logs and rolls back optimistic state.
        }
    }

    async #cancelJob(id: string): Promise<void> {
        try {
            await this.jobsListModel.cancelJob(id);
        } catch {
            // jobsListModel already logs and rolls back optimistic state.
        }
    }

    #duplicateFromJob(job: BacktestJobRecord): void {
        const newName = `Copy of ${job.spec.name}`;
        this.formModel.hydrate(job.spec);
        this.formModel.setName(newName);
        this.#dispatch({ type: 'SET_LOADED_BACKTEST_ID', id: null });
        this.logger.info('Duplicated backtest', { sourceJobId: job.id, sourceSpecId: job.specId, newName });
    }

    #buildEventBus(): BacktestWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Backtest signals
                // Header-surface signals (read by BacktestNameInput / BacktestEditButton)
                .addSignal('backtest::name', () => this.formModel.name)
                .addSignal('backtest::viewMode', () => this.#viewModeForEvents)

                // Header-surface name event
                .addEvent('backtest::setName', ({ name }) => {
                    this.formModel.setName(name);
                })

                // Editor capability signals (for nameOverflowMenu dirty indicator)
                .addSignal('backtest::editorIsDirty', () => this.formModel.isDirty)
                .addSignal('backtest::editorCanSave', () => this.formModel.isDirty)
                .addSignal('backtest::selectedBacktest', () => this.#selectedBacktest)
                .addSignal('backtest::validationErrors', () => this.formModel.validationErrors)
                // Alias for WidgetHeaderNameOverflowMenu dirty indicator
                .addSignal('widget::ideaEditorIsDirty', () => this.formModel.isDirty)

                // Editor action events
                .addEvent('backtest::editorSave', async () => {
                    try {
                        await this.#saveBacktest();
                    } catch (e) {
                        this.logger.error('Failed to save backtest', { error: sanitizeErrorForLog(e) });
                        if (e instanceof AxiosError) {
                            this.#notifyError('save-error', e);
                        }
                    }
                })
                .addEvent('backtest::editorSaveAs', async ({ name }) => {
                    try {
                        await this.#saveBacktestAs(name);
                    } catch (e) {
                        this.logger.error('Failed to save backtest as', {
                            error: sanitizeErrorForLog(e),
                            name,
                        });
                        if (e instanceof AxiosError) {
                            this.#notifyError('save-error', e);
                        }
                    }
                })
                .addEvent('backtest::editorCreateNew', () => {
                    this.#createNewBacktest();
                    this.#dispatch({ type: 'SWITCH_WIZARD_VIEW' });
                })
                .addEvent('backtest::switchToList', () => {
                    this.#dispatch({ type: 'SWITCH_LIST_VIEW' });
                })
                .addEvent('backtest::switchToWizard', () => {
                    this.#dispatch({ type: 'SWITCH_WIZARD_VIEW' });
                })
                .addEvent('backtest::viewJobResults', ({ jobId }) => {
                    this.#dispatch({ type: 'SWITCH_RESULTS_VIEW', jobId });
                    void this.resultsModel.loadResults(jobId);
                })
                .addEvent('backtest::deleteJob', ({ jobId }) => {
                    void this.#deleteJob(jobId);
                })
                .addEvent('backtest::cancelJob', ({ jobId }) => {
                    void this.#cancelJob(jobId);
                })
                .addEvent('backtest::duplicateJob', ({ jobId }) => {
                    const job = this.jobsListModel.jobs.peek().find((j) => j.id === jobId);
                    if (!job) return;
                    this.formModel.clearValidation();
                    this.#dispatch({ type: 'SWITCH_WIZARD_VIEW' });
                    this.#duplicateFromJob(job);
                })
                .addEvent('backtest::editorLoad', ({ planId }) => {
                    this.formModel.clearValidation();
                    this.#dispatch({ type: 'SWITCH_WIZARD_VIEW' });
                    void this.#loadBacktestById(planId);
                })
                .addEvent('backtest::editorDelete', ({ id }) => {
                    void this.#deleteBacktest(id);
                })

                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus);
    }
}
